<template>
    <teleport to="#search-mobile-layer-overlay-teleport">
        <!-- Dark Layer Transition -->
        <transition name="overlay-modal-animation" appear @after-enter="showModalWrapper = true" @after-leave="emit('hideMe')">
            <div
            v-if="showMe"
            class="overlay-modal-wrapper"
            :class="{'dont-close-side-modals': preventDetailsModalClose, 'second-overlay-teleport': applySecondLayer}">
                <!-- White Detail Content Transition -->
                <transition name="side-modal-animation" appear @after-leave="showMe = false" @after-enter="emit('allowPortalElementsToBeRender', true)">
                    <div
                    v-if="showModalWrapper"
                    v-click-outside="closeModal"
                    class="animation-div-wrapper"
                    :style="{'padding-bottom': getKeyboardHeight }">
                        <div ref="genericContentWraperRef" class="generic-content-wrapper">
                            <div
                            class="search-header"
                            :style="wrapperStyles"
                            :class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }">
                                <!-- Left Button -->
                                <button-tertiary
                                buttonStyle="round"
                                buttonSize="small"
                                icon="close"
                                class="position-left"
                                @clicked="[clearSearchValueOnClose ? emit('updateSearchValue', '') : null, showModalWrapper = false]"/>
                                <!-- Search Input -->
                                <input-search
                                focusInput
                                :inputValue="searchInputValue"
                                :params="{ placeholder: searchPlaceholder }"
                                @input="emit('updateSearchValue', $event)"/>
                            </div>
                            <!-- Content -->
                            <div
                            class="modal-content-wrapper"
                            @scroll="[valueScrolled = $event.target.scrollTop, emit('scrollValue', $event.target.scrollTop)]">
                                <!-- Empty State State -->
                                <empty-state-generic
                                v-if="applyEmptyState"
                                illustration="emptyState/search"
                                :title="t('emptyStates.noResults')"
                                :description="t('emptyStates.noResultsWithFiltersDescription')"/>
                                <!-- Content -->
                                <slot v-else></slot>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </transition>
    </teleport>
</template>

<script setup>
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import InputSearch from '@/components/generic/inputs/Search'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue */
import { ref, watch, onBeforeUnmount, computed } from 'vue'
import { validateIfParentExists } from '@/assets/scripts/htmlManipulators.js'
import { scrollTo } from '@/assets/scripts/scrollAnimation'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'updateSearchValue', 'allowPortalElementsToBeRender', 'scrollValue'])

/* Props
@searchPlaceholder {String} - text to display as placeholder on input
@searchInputValue {Boolean} - value being searched
@triggerClose {Boolean} - prop to trigger close of filter modal so animation happens before our modal disapears
@applyEmptyState {Boolean} - prop to apply empty-state
@clearSearchValueOnClose {Boolean} - prop to clear inputValue on modal close
*/
const props = defineProps({
    searchPlaceholder: String,
    searchInputValue: String,
    triggerClose: Boolean,
    applyEmptyState: Boolean,
    clearSearchValueOnClose: Boolean
})

const { updatePreventMobileFeatureBack, updateSideModalScroll } = mapActions()
const { getPreventMobileSideModalBackState, getKeyboardHeight, getDeviceInUse, getAppThemeMode } = mapGetters()

const showMe = ref(true)
const showModalWrapper = ref(false)

const genericContentWraperRef = ref('')
const valueScrolled = ref(0)

watch(() => props.triggerClose, () => {
    emit('allowPortalElementsToBeRender', false)
    if (props.clearSearchValueOnClose) emit('updateSearchValue', '')
    showModalWrapper.value = false
})

watch(() => props.scrollValue, (newVal, oldVal) => {
    if (newVal || newVal === 0) {
        scrollTo(genericContentWraperRef.value, props.scrollValue, null, 500, 'vertical')
        emit('clearScrollToValue')
    }
})

const validateIfClose = $event => {
    const className = 'dont-close-side-modals'
    const className2 = 'dont-close-details'
    const className3 = 'is-download-click'
    const eventHasClass = ($event.target.className && JSON.stringify($event.target.className).toLowerCase().includes(className))
    /* Download Click class */
    const eventHasClass3 = ($event.target.className && JSON.stringify($event.target.className).toLowerCase().includes(className3))
    if (validateIfParentExists($event.target, className3) || eventHasClass3) return true

    return (validateIfParentExists($event.target, className) || validateIfParentExists($event.target, className2) || eventHasClass) && !props.applySecondLayer
}

const closeModal = $event => {
    if (validateIfClose($event)) return
    /* If not, then we close our sideModal */
    emit('allowPortalElementsToBeRender', false)
    showModalWrapper.value = false
}

/*
* Computed to create a dynamic shadow to our header when we scroll our feature content
* Box-shadow is always there, we will simply manipulate it's opacity to create the ilusion it is appearing on scroll
@returns {Object} Object with styles for our header
*/
const MAX_HEIGHT_DIFFERENCE = 63 /* diference between our max/min height */
const wrapperStyles = computed(() => {
    let boxShadowAlpha = 0 /* Initial opacity for our shadow */
	const endingShadowOpacity = 0.16 /* ending opacity to help handle the current state of our shadow */

    const auxValue = valueScrolled.value > MAX_HEIGHT_DIFFERENCE ? MAX_HEIGHT_DIFFERENCE : valueScrolled.value
	const currentPercDifference = auxValue * 100 / MAX_HEIGHT_DIFFERENCE

	boxShadowAlpha = currentPercDifference * endingShadowOpacity / 100

    if (getAppThemeMode.value === 'dark') {
        return {
            'box-shadow': `0px 8px 16px rgba(0, 0, 0,${boxShadowAlpha})`
        }
    }
	return {
		'box-shadow': `0px 8px 16px rgba(36, 37, 38,${boxShadowAlpha})`
	}
})
/***********************************************************
*                                                          *
*                 MOBILE NATIVE BACK LOGIC                 *
*                                                          *
***********************************************************/
/* Preving mobile back to trigger features close */
updatePreventMobileFeatureBack(true)
onBeforeUnmount(() => {
	updatePreventMobileFeatureBack(false)
	updateSideModalScroll(0)
})

const onBackKeyDown = () => {
    if (getPreventMobileSideModalBackState.value) return
    showModalWrapper.value = false
}

document.addEventListener('backbutton', onBackKeyDown, false)
</script>

<style lang="scss" scoped>
.overlay-modal-wrapper {
    @include sideLayerOverlayTeleportStyles;
    background-color: transparent;
    z-index: 240;

    .animation-div-wrapper {
        @include searchModalAnimation;
        width: 560px;
        max-width: 100%;
        height: 100%;

        position: relative;
        border-radius: 0;
        overflow: hidden;

        .generic-content-wrapper {
            width: 100%;
            height: 100%;

            display: flex;
            flex-direction: column;

            background-color: var(--secondary-light-4);
            position: relative;

            .search-header {
                width: 100%;
                padding: 16px;

                display: flex;
                gap: 16px;

                border-bottom: 1px solid var(--secondary-light-1);

                &.mobile-status-bar-android {
                    padding-top: calc(16px + var(--android-top-padding));
                }

                &.mobile-status-bar-ios {
                    padding-top: calc(16px + constant(safe-area-inset-top));
                    padding-top: calc(16px + env(safe-area-inset-top));
                }
            }

            .modal-content-wrapper {
                width: 100%;
                height: auto;

                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                flex: 1;

                z-index: 1;
                overflow: auto;
                overflow-x: hidden;

                :deep(.empty-state-wrapper) {
                    width: 100%;
                    height: calc(100% - 24px);
                }
            }
        }
    }
}
</style>
